import { useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDailyRecapPlayer } from "@/context/DailyRecapPlayerContext";
import { FullDailyRecapPlayer } from "./FullDailyRecapPlayer";
import { format } from "date-fns";
import { RecapCoverCycler } from "./RecapStoryStrip";
import T from "@/i18n/T";

const FloatingDailyRecapPlayerBar = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { currentRecap, isPlaying, pause, resume, playRecap, currentTime, duration, recaps } = useDailyRecapPlayer();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Only show on home page (/) or daily page (/daily)
  const isHomePage = location.pathname === '/' || location.pathname === '/daily';
  
  // Show player bar if there's a current recap OR recaps are available
  const displayRecap = currentRecap || recaps[0];
  if (!isMobile || !displayRecap || !isHomePage) return null;
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const recapDate = format(new Date(displayRecap.date), "MMM d, yyyy");

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentRecap && recaps.length > 0) {
      // No current recap, start playing the first one
      playRecap(recaps[0]);
    } else if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  return (
    <>
      <div
        onClick={() => setIsExpanded(true)}
        className="w-full cursor-pointer"
      >
        {/* Thin progress bar at top */}
        <div className="h-0.5 bg-background">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Darker player bar */}
        <div className="bg-background border-t border-border/50 px-4 py-2.5">
          <div className="flex items-center gap-3">
            {/* Daily Recap artwork */}
            <div className="relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-gradient-to-br from-primary/30 via-primary/20 to-primary/10 flex items-center justify-center shadow-md">
              <div className={`w-6 h-6 rounded-full bg-primary ${isPlaying ? 'animate-pulse' : ''}`} />
              <RecapCoverCycler
                articleIds={displayRecap.article_ids}
                className="absolute inset-0 h-full w-full"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                <T k="auto.daily-tech-recap-f2ce" />
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {recapDate}
              </p>
            </div>
            
            <Button
              size="icon"
              variant="ghost"
              className="flex-shrink-0 h-10 w-10 hover:bg-primary/10"
              onClick={handleTogglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 fill-primary text-primary" />
              ) : (
                <Play className="h-5 w-5 ml-0.5 fill-primary text-primary" />
              )}
            </Button>
          </div>
        </div>
      </div>

      <FullDailyRecapPlayer
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
      />
    </>
  );
};

export default FloatingDailyRecapPlayerBar;
